import { useLocation, useHistory } from 'react-router-dom'
import { IPool } from '../../../types/reducerTypes'
import { useSelector } from 'react-redux'
import {
  selectCurrentProject,
  selectExpectedRealAllocation,
  selectTaskSubmissions,
  selectTotalAllocation,
  selectTotalAllocationToAllParticipants,
} from '../../../modules/missionControl/selectors'
import styled, { css } from 'styled-components'
import { Box } from '../../../shared_components/UI/Box'
import EmbeddableModal from '../../../shared_components/EmbeddableModal'
import { v4 as uuidv4 } from 'uuid'
import ImageVerificationTask from './Types/ImageVerification'
import {
  filterDraftsAndEmptyObjectsFromTasks,
  groupTasksAndOrder,
} from '../../../hooks/useMissionControlPoolStateManagement'
import { Check as checkIconSrc } from '../../../shared_components/Missions/assets'
import TwitterTask from './Types/Twitter'
import DiscordTask from './Types/Discord'
import OnChainTask from './Types/OnChain'
import { useCallback } from 'react'
import EmailTask from './Types/Email'
import TickUpNumber from '../../../shared_components/TickUpNumber'

import ProgressBar from '../../Home/parts/progress-bar'
import usePoolRaisePercentages from '../SwapBox/usePoolRaisePercentages'
import { numberToLocaleString } from '../../../utils/numbers'
import { showDecimalsIfNeeded } from '../../Dashboard/Allocations/InvestmentInfo'
import TelegramTask from './Types/Telegram'
import { getExchangeRatePrice } from '../SwapBox'
import { Frequency } from '../../../utils/recurring'
import RecurringTaskCard from './Types/Recurring'
import ReferralTask from './Types/Referral'
import { VariableRewardText } from '../../../shared_components/Missions/styles'
import FormTaskCard from './Types/Form'
import { useGetFormattedTaskRewardLabels } from '../../../shared_components/Missions/TaskAllocationAmount'
import MissionAction, {
  getMissionTaskType,
  useMissionActionText,
  useStartMission,
} from '../../../shared_components/Missions/MissionAction'
import useProjectStatus from '../../../shared_components/Missions/useProjectStatus'
import TaskDescription from '../../../shared_components/Missions/TaskDescription'
import {
  NewTaskType,
  SubmissionsByTask,
  Task,
} from '../../../shared_components/Missions/types'
import GraphIndexerTask from './Types/GraphIndexer'
import VerifyWalletTask from './Types/VerifyWallet'
import { TaskCard } from '../../../shared_components/Library/Card'

const Container = styled(Box)`
  height: auto;
  margin-bottom: 4rem;
  padding-top: 3.5rem;
  h2.page-title {
    color: #fff;
    font-variant-numeric: ordinal;
    font-feature-settings: 'dlig' on, 'ss01' on, 'dnom' on;
    font-family: 'Space Grotesk';
    font-size: 48px;
    font-style: normal;
    font-weight: 700;
    letter-spacing: -1.2px;
  }
  span.intro {
    color: #fff;
    font-variant-numeric: ordinal;
    font-feature-settings: 'dlig' on, 'ss01' on, 'dnom' on;
    font-family: 'Space Grotesk';
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 30px; /* 150% */
    letter-spacing: -0.8px;
    max-width: 750px;
    padding-bottom: 48px;
  }

  .back-box {
    width: auto;
    margin-right: auto;

    cursor: pointer;
    svg {
      transform: rotate(180deg);
      path {
        stroke: white;
      }
    }

    :hover,
    :active {
      color: #f4ed0c;

      svg {
        path {
          stroke: #f4ed0c;
        }
      }
    }
  }

  @media (max-width: 1000px) {
    padding: 2rem 0.5rem 4rem;
    gap: 12px;
    h1.page-title {
      font-size: 38px;
      line-height: 40px;
    }
    span.intro {
      font-size: 18px;
      line-height: 26px;
      padding-bottom: 28px;
    }
    .back-box {
      padding-left: 0.75rem;
    }
  }
`

const TaskContainer = styled(Box)`
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(3, 11, 17, 0.9);
  padding: 24px 40px;

  @media screen and (max-width: 1000px) {
    padding: 20px;
  }
`

const TaskDivider = styled.hr`
  height: 1px;
  width: 100%;
  border: none;
  opacity: 0.2;
  background: rgba(255, 255, 255, 0.5);
`

const Missions = ({ pool }: { pool: IPool }) => {
  const currentProject = useSelector(selectCurrentProject)
  const taskSubmissions = useSelector(selectTaskSubmissions)
  const { tasks } = currentProject

  const location = useLocation()

  const history = useHistory()
  const searchParams = new URLSearchParams(location.search)

  const updateSearchParam = (paramName: string, value: string) => {
    searchParams.set(paramName, value)
    history.push(`${location.pathname}?${searchParams.toString()}`)
  }

  const activeMission = searchParams.get('active_mission')

  function dismissModal() {
    updateSearchParam('active_mission', '')
  }

  const taskId = Number(activeMission ?? 0)

  const [activeTasks, _, completedTasks, expiredTasks] = groupTasksAndOrder(
    filterDraftsAndEmptyObjectsFromTasks(tasks)
      .slice()
      .sort((a, b) => b.id - a.id) ?? [],
    taskSubmissions ?? ({} as SubmissionsByTask),
  )

  const isEthRaise = pool?.poolSettings?.raiseCurrency === 'eth'
  const isFormMission = !!activeTasks.find(
    (task) => !!task.subtasks?.[0]?.formMissionId,
  )

  const { start, end } = pool?.privateSale ?? {}
  const projectStatus = useProjectStatus({ start, end })

  return (
    <Container dc fu g={32}>
      <h2 className="page-title">Increase your Whitelist amount</h2>
      <span className="intro">
        Congratulations, you have been registered to participate in the{' '}
        {pool.name.toUpperCase()} Token Sale. Here are your Whitelist amount
        details.
      </span>
      <YourInvestment isEthRaise={isEthRaise} pool={pool} />
      <PoolOversubscribedBy
        poolAddress={pool?.poolAddress}
        totalSupplyForSale={pool?.poolSettings?.totalSupplyForSale}
        price={pool?.privateSale?.price}
      />
      <TaskContainer dc afs g={16}>
        {activeTasks.map((task, index) => (
          <Box dc g={32} key={uuidv4()}>
            <TaskComponent
              task={task}
              isEthRaise={isEthRaise}
              projectStatus={projectStatus}
            />
            {index < activeTasks.length - 1 && <TaskDivider />}
          </Box>
        ))}
        {completedTasks.map((task) => (
          <Box dc g={32} key={uuidv4()}>
            <TaskDivider />
            <TaskComponent
              task={task}
              isComplete
              isEthRaise={isEthRaise}
              projectStatus={projectStatus}
            />
          </Box>
        ))}
        {expiredTasks.map((task) => (
          <Box dc g={32} key={uuidv4()}>
            <TaskDivider />
            <TaskComponent
              task={task}
              isExpired
              isEthRaise={isEthRaise}
              projectStatus={projectStatus}
            />
          </Box>
        ))}
      </TaskContainer>
      <EmbeddableModal
        minWidth={isFormMission ? 1024 : 470}
        dismiss={dismissModal}
        isVisible={!!activeMission}
        isClipped={!isFormMission}
      >
        {taskId && (
          <TaskBody
            id={taskId}
            dismiss={dismissModal}
            isEthRaise={isEthRaise}
          />
        )}
      </EmbeddableModal>
    </Container>
  )
}

export default Missions

function TaskComponent({
  task,
  isComplete,
  isExpired,
  isEthRaise,
  projectStatus,
}: {
  task: Task
  isComplete?: boolean
  isExpired?: boolean
  isEthRaise?: boolean
  projectStatus?: 'ended' | 'active' | 'coming-soon'
}) {
  const { internalName: description, subtasks } = task
  const coreTask = subtasks?.[0]

  const currentProject = useSelector(selectCurrentProject)
  const projectId = currentProject?.id

  const getFormattedReward = useGetFormattedTaskRewardLabels({
    taskId: task.id,
  })

  const startMission = useStartMission(task.id)
  const { isReferralTask, isRecurringMission, isManualAPIRewardTask } =
    getMissionTaskType({
      task,
    })
  const actionText = useMissionActionText({ task, isComplete })

  if (!coreTask) return <></>

  if (isRecurringMission) {
    return (
      <RecurringTaskCard
        task={task}
        isComplete={isComplete}
        onClick={startMission}
        actionText={actionText}
        projectStatus={projectStatus}
      />
    )
  }

  const { formattedReward, formattedMaxReward, formattedTiers } =
    getFormattedReward({
      overrideReward: isManualAPIRewardTask && !isComplete ? 0 : undefined,
      includePlus: !isComplete && !isManualAPIRewardTask,
    })

  const projectInactive = projectStatus !== 'active'

  return (
    <TaskCard
      fw
      ac
      isFaded={
        isComplete || isExpired || projectInactive || isManualAPIRewardTask
      }
    >
      <Box g={40} ac>
        <Box dc g={6}>
          {isComplete && <Checkbox mt={4} />}
          <Box dc g={4} className="task-info">
            <span className="reward-amount">
              <span className="allocation-amount">{formattedReward}</span>
              {formattedMaxReward && (
                <VariableRewardText>{formattedMaxReward}</VariableRewardText>
              )}
              {formattedTiers && (
                <VariableRewardText>{formattedTiers}</VariableRewardText>
              )}
            </span>
            <TaskDescription>{description}</TaskDescription>
          </Box>
        </Box>
        <MissionAction taskId={task.id} projectStatus={projectStatus} />
      </Box>
    </TaskCard>
  )
}

const InvestmentContainer = styled(Box)`
  width: 100%;
  padding: 24px 40px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(3, 11, 17, 0.95);

  position: relative;
  ::before {
    content: '';
    position: absolute;
    background: linear-gradient(to bottom, transparent, #04111b);
    width: 150vw;
    height: 500px;
    top: -400px;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
    z-index: -1;
  }

  h4.allocation {
    color: #fff;
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 20px;
    font-style: normal;
    font-weight: 300;
    line-height: 25px; /* 125% */
    letter-spacing: 6px;
    text-transform: uppercase;
  }
  span.amount,
  span.currency {
    color: ${({ theme }) => theme.colors.brandPrimary};
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.18px;
    text-transform: uppercase;
  }

  span.amount.wide {
    padding: 10px 0;
  }
`

const InvestmentContainerMobile = styled(Box)`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(3, 11, 17, 0.95);

  h4.allocation {
    color: #fff;
    text-align: center;
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: 22px; /* 137.5% */
    letter-spacing: 4.8px;
    text-transform: uppercase;
  }
  span.amount,
  span.currency {
    color: ${({ theme }) => theme.colors.brandPrimary};
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.18px;
    text-transform: uppercase;
  }
`

const AllocPrice = styled(Box)`
  --button-border-color: rgba(255, 255, 255, 0.2);
  border: 1px solid var(--button-border-color);
  padding: 10px 12px;
  width: 210px;
  text-align: center;
  justify-content: center;
`

const InfoBlock = styled(Box)<{ isFirst?: boolean }>`
  align-items: flex-start;
  span.title {
    color: #fff;
    font-variant-numeric: ordinal;
    font-feature-settings: 'dlig' on, 'ss01' on, 'dnom' on;
    font-family: 'Space Grotesk';
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 30px; /* 150% */
    letter-spacing: -0.8px;
    opacity: 0.5;
  }
  span.amount,
  span.currency {
    color: ${({ theme }) => theme.colors.brandPrimary};
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.18px;
    text-transform: uppercase;
  }
  span.amount.wide {
    padding: 10px 0;
  }

  padding-right: 1rem;
  padding-left: 1rem;

  border-left: 1px solid rgba(255, 255, 255, 0.1);

  @media screen and (max-width: 1000px) {
    border-left: none;

    span.title {
      font-size: 14px;
    }
    span.amount,
    span.currency {
      font-size: 16px;
    }
  }
`

const Desktop = styled.div<{ w?: string }>`
  @media screen and (max-width: 1000px) {
    display: none;
  }
  ${(props) =>
    props.w &&
    css`
      width: ${props.w};
    `}
`

const Mobile = styled.div`
  width: 100%;
  @media screen and (min-width: 1000px) {
    display: none;
  }
`

function YourInvestment({
  isEthRaise,
  pool,
}: {
  isEthRaise: boolean
  pool: IPool
}) {
  const totalAllocation = useSelector(selectTotalAllocation)
  const totalAllocationToAllParticipants = useSelector(
    selectTotalAllocationToAllParticipants,
  )
  const expectedRealAllocation = useSelector(selectExpectedRealAllocation)

  const isTbc = pool?.isTbc
  const raiseCurrency = isEthRaise ? 'ETH' : 'USDB'
  const pricePerTokenExchange = isTbc
    ? 'TBD'
    : getExchangeRatePrice({
        ticker: pool?.ticker,
        price: pool?.privateSale?.price,
        poolSettings: pool?.poolSettings,
      })

  return (
    <>
      <Desktop w="100%">
        <InvestmentContainer ac g={16}>
          <Box dc g={16} afs className="alloc-block">
            <h4 className="allocation">Your Whitelist amount</h4>
            <AllocPrice g={8} afe className="alloc-price">
              <span className="amount">
                <TickUpNumber amount={totalAllocation} />
              </span>
              <span className="currency">{raiseCurrency}</span>
            </AllocPrice>
          </Box>
          <InfoBlock pss jsb dc g={8} isFirst className="hide-mobile">
            <span className="title">
              Total Whitelist amount to all participants
            </span>
            <span className="amount wide">
              {showDecimalsIfNeeded(
                totalAllocationToAllParticipants,
                isEthRaise ? 8 : 2,
              )}{' '}
              <span className="currency">{raiseCurrency}</span>
            </span>
          </InfoBlock>
          <InfoBlock pss jsb dc g={8}>
            <span className="title">Your expected real Whitelist amount</span>
            <span className="amount wide">
              {showDecimalsIfNeeded(expectedRealAllocation, isEthRaise ? 8 : 2)}{' '}
              <span className="currency">{raiseCurrency}</span>
            </span>
          </InfoBlock>
          <InfoBlock pss jsb dc g={8} className="hide-mobile">
            <span className="title">Price</span>
            <span className="amount wide">{pricePerTokenExchange}</span>
          </InfoBlock>
        </InvestmentContainer>
      </Desktop>
      <Mobile>
        <Box dc ac g={22}>
          <InvestmentContainerMobile dc ac g={16}>
            <Box dc g={16} ac className="alloc-block">
              <h4 className="allocation">Your Whitelist amount</h4>
              <AllocPrice g={8} afe className="alloc-price">
                <span className="amount">
                  <TickUpNumber amount={totalAllocation} />
                </span>
                <span className="currency">{raiseCurrency}</span>
              </AllocPrice>
            </Box>
          </InvestmentContainerMobile>
          <InvestmentContainerMobile dc g={12}>
            <InfoBlock dc g={8} isFirst>
              <span className="title">
                Total Whitelist amount to all participants
              </span>
              <span className="amount">
                {numberToLocaleString(totalAllocationToAllParticipants)}{' '}
                <span className="currency">{raiseCurrency}</span>
              </span>
            </InfoBlock>
            <TaskDivider />
            <InfoBlock dc g={8}>
              <span className="title">Your expected real Whitelist amount</span>
              <span className="amount">
                {numberToLocaleString(expectedRealAllocation)}{' '}
                <span className="currency">{raiseCurrency}</span>
              </span>
            </InfoBlock>
            <TaskDivider />
            <InfoBlock dc g={8}>
              <span className="title">Price</span>
              <span className="amount">{pricePerTokenExchange}</span>
            </InfoBlock>
          </InvestmentContainerMobile>
        </Box>
      </Mobile>
    </>
  )
}

function TaskBody({
  id,
  isEthRaise = false,
  dismiss,
}: {
  id: number
  isEthRaise?: boolean
  dismiss: () => void
}) {
  const currentProject = useSelector(selectCurrentProject)

  const task = currentProject.tasks?.find((task) => task.id === id)

  if (!task) return <></>

  const coreTask = task?.subtasks?.[0]

  if (!coreTask) return <></>

  const isImageVerification = !!coreTask?.imageVerification

  if (isImageVerification)
    return (
      <ImageVerificationTask subtask={coreTask} taskId={id} onClose={dismiss} />
    )

  const isTwitterTask = coreTask?.requiresSocial === 'Twitter'

  if (isTwitterTask) return <TwitterTask subtask={coreTask} taskId={id} />

  const isDiscordTask = coreTask?.requiresSocial === 'Discord'

  if (isDiscordTask) return <DiscordTask subtask={coreTask} taskId={id} />

  const isEmailTask = coreTask?.isEmailTask

  if (isEmailTask) return <EmailTask subtask={coreTask} taskId={id} />

  const isOnChainTask = coreTask?.onChainTask

  if (isOnChainTask) return <OnChainTask subtask={coreTask} taskId={id} />

  const isGraphIndexerTask = coreTask?.newTaskType === NewTaskType.GRAPH_INDEXER

  if (isGraphIndexerTask)
    return <GraphIndexerTask subtask={coreTask} taskId={id} />

  const isVerifyWalletTask = coreTask?.newTaskType === NewTaskType.VERIFY_WALLET

  if (isVerifyWalletTask)
    return <VerifyWalletTask subtask={coreTask} taskId={id} />

  const isTelegramTask = coreTask?.requiresSocial === 'Telegram'

  if (isTelegramTask) return <TelegramTask subtask={coreTask} taskId={id} />

  const isFormMission = !!coreTask?.formMissionId

  if (isFormMission) {
    const { metaData } = task.subtasks[0]
    let reward = task.pointReward
    let maxReward
    if (metaData && 'rewardPerMetric' in metaData && 'maxReward' in metaData) {
      reward = metaData.rewardPerMetric
      maxReward = metaData.maxReward
    }

    return (
      <FormTaskCard
        subtask={coreTask}
        taskId={id}
        isEthRaise={isEthRaise}
        reward={reward}
        maxReward={maxReward}
      />
    )
  }

  const isReferralTask = !!coreTask?.isReferralTask

  if (isReferralTask)
    return <ReferralTask subtask={coreTask} taskId={id} projectId={currentProject.id} />

  return <Box dc>TODO - other task types</Box>
}

export function Checkbox({ mt }: { mt?: number }) {
  return (
    <Box c fu aw marginTop={mt}>
      <img src={checkIconSrc} width={20} height={20} alt="check icon" />
    </Box>
  )
}

const PoolOversubscribedCont = styled(Box)`
  h4 {
    color: #fff;
    font-family: 'Space Grotesk';
    font-size: 28px;
    font-style: normal;
    font-weight: 700;
    line-height: 32px; /* 114.286% */
  }

  p {
    color: #fff;
    font-feature-settings: 'dlig' on;
    font-family: 'Space Grotesk';
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 30px; /* 150% */
    letter-spacing: -0.8px;
    opacity: 0.5;
    max-width: 722px;
  }
`

function PoolOversubscribedBy({
  poolAddress,
  totalSupplyForSale,
  price,
}: {
  poolAddress: IPool['poolAddress']
  totalSupplyForSale: IPool['poolSettings']['totalSupplyForSale']
  price: IPool['privateSale']['price']
}) {
  const { percentSoftCap, isDiluted } = usePoolRaisePercentages({
    poolAddress,
    totalSupplyForSale,
    price,
  })
  const percentage = percentSoftCap
  const poolOversubscribedBy = Math.round(percentage / 100) ?? 1
  return (
    <>
      {poolOversubscribedBy > 1 && (
        <PoolOversubscribedCont dc g={24}>
          <h4>
            Pool is predicted to be {poolOversubscribedBy}x oversubscribed
          </h4>
          <ProgressBar
            clearBg
            isFatter
            isDiluted={isDiluted}
            displayText={String(percentage)}
            percentage={percentage ? Math.min(Math.max(percentage, 0), 100) : 0}
          />
          <p>
            The pool is likely to oversubscribe, meaning your Whitelist
            allocation will be partially refunded and will receive a % of your
            tokens. Increase your allocation by completing the missions below.
          </p>
        </PoolOversubscribedCont>
      )}
    </>
  )
}
